import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const PWA_DISMISS_KEY = 'pwaInstallDismissed';

@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  private deferredPrompt: any = null;
  private showBannerSubject = new BehaviorSubject<boolean>(false);
  showBanner$ = this.showBannerSubject.asObservable();

  isIos = false;

  constructor(private readonly ngZone: NgZone) {
    this.isIos = this.detectIos();
  }

  init(): void {
    if (this.isRunningStandalone() || this.isDismissed()) {
      return;
    }

    if (this.isIos) {
      this.showBannerSubject.next(true);
      return;
    }

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event;
      this.ngZone.run(() => {
        this.showBannerSubject.next(true);
      });
    });

    window.addEventListener('appinstalled', () => {
      this.ngZone.run(() => {
        this.deferredPrompt = null;
        this.showBannerSubject.next(false);
      });
    });
  }

  async installApp(): Promise<void> {
    if (!this.deferredPrompt) {
      return;
    }
    this.deferredPrompt.prompt();
    const result = await this.deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      this.deferredPrompt = null;
      this.showBannerSubject.next(false);
    }
  }

  dismissBanner(): void {
    this.showBannerSubject.next(false);
    localStorage.setItem(PWA_DISMISS_KEY, 'true');
  }

  private isRunningStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
  }

  private isDismissed(): boolean {
    return localStorage.getItem(PWA_DISMISS_KEY) === 'true';
  }

  private detectIos(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isLegacyIos = /iphone|ipad|ipod/.test(userAgent) && !(window as any).MSStream;
    // iPadOS 13+ reports a macOS user agent; detect via touch support on Apple platform
    const isIpadOs13Plus = /macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
    return isLegacyIos || isIpadOs13Plus;
  }
}
